import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { type UUID } from '~/types/common';

import { Alert, Fieldset, FormRow, Hint } from '@vestigas/ui';
import { type FormStateHandler } from '@vestigas/ui/organisms';
import { useAppForm } from '~/ui/molecules/TanstackForm';

import { zodLedgerAccountImportFormSchema } from './validations';

type LedgerAccountImportFormValues = {
  applyToAllCompanies: boolean;
  companyIds?: UUID[];
  validFrom?: string;
};

export type LedgerAccountImportFormContext = {
  readonly formData?: Partial<LedgerAccountImportFormValues>;
};

type P = {
  readonly context: LedgerAccountImportFormContext;
  readonly onFormChange: FormStateHandler;
};

function selectApplyToAllCompanies(state: {
  values: { applyToAllCompanies: boolean };
}) {
  return state.values.applyToAllCompanies;
}

export function LedgerAccountImportForm({ context, onFormChange }: P) {
  const { t } = useTranslation('ledgerAccounts');
  const { formData } = context;

  const defaultValues: LedgerAccountImportFormValues = {
    applyToAllCompanies: formData?.applyToAllCompanies ?? false,
    companyIds: formData?.companyIds ?? [],
    validFrom: formData?.validFrom ?? '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit() {
      // Submission is owned by DataImport.
    },
    validators: {
      onChange: zodLedgerAccountImportFormSchema,
    },
  });

  useEffect(() => {
    function notifyParent() {
      const { isValid, values: data } = form.store.state;

      onFormChange({
        data,
        errors: {},
        isValid:
          isValid && zodLedgerAccountImportFormSchema.safeParse(data).success,
      });
    }

    notifyParent();
    const subscription = form.store.subscribe(notifyParent);

    return () => subscription.unsubscribe();
  }, [form.store, onFormChange]);

  return (
    <Fieldset legend={t(($) => $.import.form.title)}>
      <Alert severity="info">
        <span className="whitespace-pre-line">
          {t(($) => $.import.instructions.replacementNotice)}
        </span>
      </Alert>
      <FormRow vAlign="top">
        <form.Subscribe selector={selectApplyToAllCompanies}>
          {(applyToAllCompanies) => (
            <form.AppField name="companyIds">
              {(field) => (
                <field.MultiSelectCompaniesField
                  className="flex-1"
                  isDisabled={applyToAllCompanies}
                  label={t(($) => $.import.form.companyIds.label)}
                  placeholder={t(($) => $.import.form.companyIds.placeholder)}
                />
              )}
            </form.AppField>
          )}
        </form.Subscribe>

        <form.AppField
          name="applyToAllCompanies"
          listeners={{
            onChange: ({ fieldApi, value: applyToAllCompanies }) => {
              if (applyToAllCompanies) {
                fieldApi.form.setFieldValue('companyIds', []);
              }
            },
          }}
        >
          {(field) => (
            <field.SwitchField
              className="mt-5 w-fit shrink-0"
              label={t(($) => $.import.form.applyToAllCompanies.label)}
            />
          )}
        </form.AppField>
      </FormRow>

      <FormRow vAlign="top">
        <form.AppField name="validFrom">
          {(field) => (
            <field.TextField
              className="w-44 shrink-0"
              label={t(($) => $.import.form.validFrom.label)}
              type="date"
            />
          )}
        </form.AppField>
        <Hint className="mt-5 flex-1">
          {t(($) => $.import.form.validFrom.hint)}
        </Hint>
      </FormRow>
    </Fieldset>
  );
}
